import { CATALOG, catalogById, resolveVerseId } from "../deck/catalog";
import {
  addCount,
  clampCounts,
  countsFromIds,
  mergeMinimum,
  ownedCount,
  uniqueIds,
  type CardCounts,
} from "./counts";
import {
  artIdsForChapterFinale,
  artIdsForMissionClear,
  artIdsForOwnVerse,
} from "./gallery";
import { MAX_COPIES, STARTER_OWNED_IDS } from "./starter";
import type { CollectionSave } from "./types";

const STORAGE_KEY = "sots-collection-v1";
/** Read-only peek at campaign save for migration + art reconcile. */
const CAMPAIGN_KEY = "sots-campaign-proclaim-v1";

/**
 * Finale mission id → chapter id for gallery chapter-finale pieces.
 * Mission-clear pieces are derived from the gallery catalog directly.
 */
const FINALE_MISSION_CHAPTER: Readonly<Record<string, string>> = {
  "neh-joy-of-the-lord": "neh-wall",
  "gid-pitchers": "gideon-300",
};

function defaultSave(): CollectionSave {
  const ownedCounts = countsFromIds(STARTER_OWNED_IDS);
  return {
    version: 1,
    ownedCounts,
    unlockedArt: artForOwned(ownedCounts),
  };
}

type CampaignMissionFlags = {
  easy?: boolean;
  standard?: boolean;
  hard?: boolean;
};

function isAnyDifficultyCleared(flags: CampaignMissionFlags | undefined): boolean {
  return !!(flags?.easy || flags?.standard || flags?.hard);
}

/** Collect gallery art ids earned by campaign clears (mission + finale). */
function artIdsFromCampaignMissions(
  missions: Record<string, CampaignMissionFlags> | undefined,
): string[] {
  if (!missions) return [];
  const ids = new Set<string>();
  for (const [missionId, flags] of Object.entries(missions)) {
    if (!isAnyDifficultyCleared(flags)) continue;
    for (const id of artIdsForMissionClear(missionId)) ids.add(id);
    const chapterId = FINALE_MISSION_CHAPTER[missionId];
    if (chapterId) {
      for (const id of artIdsForChapterFinale(chapterId)) ids.add(id);
    }
  }
  return [...ids];
}

function artForOwned(owned: CardCounts): string[] {
  const ids: string[] = [];
  for (const id of uniqueIds(owned)) {
    ids.push(...artIdsForOwnVerse(id));
  }
  return [...new Set(ids)];
}

function sanitizeCounts(raw: unknown): CardCounts {
  if (!raw || typeof raw !== "object") return {};
  const allowed = new Set(CATALOG.map((v) => v.id));
  const out: CardCounts = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const id = resolveVerseId(k);
    if (!allowed.has(id)) continue;
    const n = typeof v === "number" ? Math.floor(v) : 0;
    if (n <= 0) continue;
    // Prefer the higher count if both legacy + new keys appear
    out[id] = Math.min(MAX_COPIES, Math.max(out[id] ?? 0, n));
  }
  return out;
}

function sanitizeArt(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const x of raw) {
    if (typeof x !== "string" || !x.length) continue;
    // Gallery pieces keyed verse:{id} — remap legacy verse ids
    let id = x;
    if (id.startsWith("verse:")) {
      id = `verse:${resolveVerseId(id.slice("verse:".length))}`;
    }
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

function sanitize(raw: unknown): CollectionSave {
  const base = defaultSave();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Partial<CollectionSave>;
  let ownedCounts = sanitizeCounts(o.ownedCounts);
  ownedCounts = mergeMinimum(ownedCounts, STARTER_OWNED_IDS);
  ownedCounts = clampCounts(ownedCounts, MAX_COPIES);
  const unlockedArt = [
    ...new Set([...sanitizeArt(o.unlockedArt), ...artForOwned(ownedCounts)]),
  ];
  return { version: 1, ownedCounts, unlockedArt };
}

/** Pull owned cards from legacy campaign save (no full-catalog grant). */
function migrateFromCampaign(): CollectionSave | null {
  try {
    const raw = localStorage.getItem(CAMPAIGN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      ownedCounts?: unknown;
      ownedCardIds?: unknown;
      missions?: Record<string, CampaignMissionFlags>;
    };
    let owned: CardCounts = {};
    if (parsed.ownedCounts && typeof parsed.ownedCounts === "object") {
      owned = sanitizeCounts(parsed.ownedCounts);
    } else if (Array.isArray(parsed.ownedCardIds)) {
      owned = countsFromIds(
        parsed.ownedCardIds.filter((id): id is string => typeof id === "string"),
      );
      owned = sanitizeCounts(owned);
    }
    if (uniqueIds(owned).length === 0) return null;

    owned = mergeMinimum(owned, STARTER_OWNED_IDS);
    owned = clampCounts(owned, MAX_COPIES);
    const unlockedArt = new Set([
      ...artForOwned(owned),
      ...artIdsFromCampaignMissions(parsed.missions),
    ]);

    return {
      version: 1,
      ownedCounts: owned,
      unlockedArt: [...unlockedArt],
    };
  } catch {
    return null;
  }
}

/**
 * Grant any gallery art the player already earned in campaign but is missing
 * from collection (e.g. new chapter pieces added after a clear).
 * Returns true if the save was mutated.
 */
function reconcileCampaignArt(save: CollectionSave): boolean {
  try {
    const raw = localStorage.getItem(CAMPAIGN_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as {
      missions?: Record<string, CampaignMissionFlags>;
    };
    const earned = artIdsFromCampaignMissions(parsed.missions);
    if (!earned.length) return false;
    const have = new Set(save.unlockedArt);
    let changed = false;
    for (const id of earned) {
      if (have.has(id)) continue;
      save.unlockedArt.push(id);
      have.add(id);
      changed = true;
    }
    return changed;
  } catch {
    return false;
  }
}

function readRaw(): CollectionSave {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const save = sanitize(JSON.parse(raw));
      if (reconcileCampaignArt(save)) writeSave(save);
      return save;
    }
  } catch {
    /* ignore */
  }
  const migrated = migrateFromCampaign();
  if (migrated) {
    writeSave(migrated);
    return migrated;
  }
  const fresh = defaultSave();
  writeSave(fresh);
  return fresh;
}

function writeSave(save: CollectionSave): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(save));
}

export function loadCollection(): CollectionSave {
  const save = readRaw();
  return {
    version: 1,
    ownedCounts: { ...save.ownedCounts },
    unlockedArt: [...save.unlockedArt],
  };
}

export function saveCollection(save: CollectionSave): void {
  writeSave({
    version: 1,
    ownedCounts: clampCounts(
      mergeMinimum(sanitizeCounts(save.ownedCounts), STARTER_OWNED_IDS),
      MAX_COPIES,
    ),
    unlockedArt: sanitizeArt(save.unlockedArt),
  });
}

/** Reset collection to starter only (dev / full wipe). */
export function resetCollection(): CollectionSave {
  const fresh = defaultSave();
  writeSave(fresh);
  return loadCollection();
}

export function copiesOwned(verseId: string): number {
  return ownedCount(loadCollection().ownedCounts, verseId);
}

export function isOwned(verseId: string): boolean {
  return copiesOwned(verseId) > 0;
}

export function ownedVerseIds(): string[] {
  return uniqueIds(loadCollection().ownedCounts);
}

export function isArtUnlocked(artId: string): boolean {
  return loadCollection().unlockedArt.includes(artId);
}

export function unlockArt(artIds: readonly string[]): string[] {
  if (!artIds.length) return [];
  const save = loadCollection();
  const newly: string[] = [];
  for (const id of artIds) {
    if (!id || save.unlockedArt.includes(id)) continue;
    save.unlockedArt.push(id);
    newly.push(id);
  }
  if (newly.length) writeSave(save);
  return newly;
}

/**
 * Grant copies of verse ids (capped at MAX_COPIES).
 * Unlocks verse-face gallery art for any id that ends with at least 1 copy.
 * Returns ids that actually gained a copy (for reward UI).
 */
export function grantCards(ids: readonly string[]): string[] {
  if (!ids.length) return [];
  const save = loadCollection();
  const gained: string[] = [];
  for (const id of ids) {
    if (!catalogById(id)) continue;
    const before = ownedCount(save.ownedCounts, id);
    if (before >= MAX_COPIES) {
      // Still ensure art unlock
      for (const artId of artIdsForOwnVerse(id)) {
        if (!save.unlockedArt.includes(artId)) save.unlockedArt.push(artId);
      }
      continue;
    }
    save.ownedCounts = addCount(save.ownedCounts, id, 1);
    // clamp single id
    const n = ownedCount(save.ownedCounts, id);
    if (n > MAX_COPIES) save.ownedCounts[id] = MAX_COPIES;
    if (ownedCount(save.ownedCounts, id) > before) gained.push(id);
    for (const artId of artIdsForOwnVerse(id)) {
      if (!save.unlockedArt.includes(artId)) save.unlockedArt.push(artId);
    }
  }
  save.ownedCounts = mergeMinimum(save.ownedCounts, STARTER_OWNED_IDS);
  writeSave(save);
  return gained;
}

/** Sync collection after a mission victory (art + optional signature). */
export function onMissionVictory(opts: {
  missionId: string;
  chapterId?: string;
  isFinale?: boolean;
  signatureVerseId?: string;
  /** True if this difficulty was already cleared before. */
  alreadyCleared: boolean;
}): { artUnlocked: string[]; signatureGranted: string[] } {
  // Gallery art is idempotent — always try (covers new pieces after a prior clear).
  const artUnlocked: string[] = [];
  artUnlocked.push(...unlockArt(artIdsForMissionClear(opts.missionId)));
  if (opts.isFinale && opts.chapterId) {
    artUnlocked.push(...unlockArt(artIdsForChapterFinale(opts.chapterId)));
  }

  if (opts.alreadyCleared) {
    return { artUnlocked: [...new Set(artUnlocked)], signatureGranted: [] };
  }

  const signatureGranted: string[] = [];
  if (opts.isFinale && opts.signatureVerseId) {
    const before = copiesOwned(opts.signatureVerseId);
    if (before < 1) {
      signatureGranted.push(...grantCards([opts.signatureVerseId]));
    } else {
      // Ensure face art even if somehow owned without art
      unlockArt(artIdsForOwnVerse(opts.signatureVerseId));
    }
  }

  return {
    artUnlocked: [...new Set(artUnlocked)],
    signatureGranted,
  };
}

/** One of each owned id (for default campaign deckCounts). */
export function oneOfEachOwned(): CardCounts {
  const out: CardCounts = {};
  for (const id of ownedVerseIds()) out[id] = 1;
  return out;
}
